import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  TextField,
  Alert,
  Snackbar,
  CircularProgress,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon, Download as DownloadIcon } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { publishersService, PublisherGrantsReportRow } from '../services/firebaseFunctions';

function escapeCsvCell(value: string | number | boolean): string {
  const s = String(value);
  if (/[",\n\r]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

function buildGrantsReportCsv(rows: PublisherGrantsReportRow[]): string {
  const headers = ['Nome da empresa', 'Nome da editora', 'Nome do título', 'Quantidade'];
  const lines = [headers.join(',')];
  for (const r of rows) {
    lines.push(
      [escapeCsvCell(r.companyName), escapeCsvCell(r.publisherName), escapeCsvCell(r.titleName), escapeCsvCell(r.quantity)].join(',')
    );
  }
  return lines.join('\n');
}

const PublisherGrantsReportPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [publisherName, setPublisherName] = useState<string | null>(null);
  const [loadingMeta, setLoadingMeta] = useState(true);
  const [dateStart, setDateStart] = useState('');
  const [dateEnd, setDateEnd] = useState('');
  const [exporting, setExporting] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error',
  });

  const loadPublisher = useCallback(async () => {
    if (!id) return;
    try {
      setLoadingMeta(true);
      const list = await publishersService.getAll();
      const p = list.find((x) => x.id === id);
      setPublisherName(p ? p.name : null);
      if (!p) {
        setSnackbar({ open: true, message: 'Editora não encontrada', severity: 'error' });
      }
    } catch {
      setSnackbar({ open: true, message: 'Erro ao carregar editora', severity: 'error' });
    } finally {
      setLoadingMeta(false);
    }
  }, [id]);

  useEffect(() => {
    loadPublisher();
  }, [loadPublisher]);

  const datesValid =
    /^\d{4}-\d{2}-\d{2}$/.test(dateStart) && /^\d{4}-\d{2}-\d{2}$/.test(dateEnd) && dateStart <= dateEnd;

  const handleDownloadCsv = async () => {
    if (!id || !datesValid) return;
    try {
      setExporting(true);
      const res = await publishersService.getGrantsReportByPeriod({
        publisherId: id,
        dateStart,
        dateEnd,
      });
      if (!res.success || !res.data) {
        throw new Error(res.error || 'Falha ao gerar relatório');
      }
      const csv = buildGrantsReportCsv(res.data.rows);
      const blob = new Blob(['\ufeff', csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const safeSlug = (res.data.publisherName || 'editora').replace(/[^\w-]+/g, '_').slice(0, 80);
      a.download = `relatorio-concessoes-${safeSlug}-${dateStart}-${dateEnd}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      setSnackbar({ open: true, message: 'CSV baixado com sucesso.', severity: 'success' });
    } catch (e) {
      setSnackbar({
        open: true,
        message: e instanceof Error ? e.message : 'Erro ao exportar',
        severity: 'error',
      });
    } finally {
      setExporting(false);
    }
  };

  if (!id) {
    return null;
  }

  if (loadingMeta) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(`/publishers/${id}`)}>
          Voltar
        </Button>
        <Typography variant="h4" sx={{ fontWeight: 600, flex: 1 }}>
          Relatório de concessões
        </Typography>
      </Box>

      <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 2 }}>
        {publisherName ?? 'Editora'} — período em horário de Brasília (concessões efetivas e pendentes no intervalo).
      </Typography>

      <Paper sx={{ p: 3, maxWidth: 480 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="Data inicial"
            type="date"
            value={dateStart}
            onChange={(e) => setDateStart(e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
          <TextField
            label="Data final"
            type="date"
            value={dateEnd}
            onChange={(e) => setDateEnd(e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
          <Button
            variant="contained"
            startIcon={exporting ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
            onClick={handleDownloadCsv}
            disabled={!publisherName || !datesValid || exporting}
          >
            {exporting ? 'Gerando…' : 'Baixar CSV'}
          </Button>
        </Box>
      </Paper>

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PublisherGrantsReportPage;
